import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatMs } from "@/utils/format";
import type { NormalizedTrace, TraceLane, TraceNode } from "@/utils/trace/model";
import { SubAgentInlineSummary, laneNodeFor } from "./SubAgentSummary";

function laneHeader(
  lane: TraceLane,
  selectedNodeId: string | null,
  onSelect: (node: TraceNode) => void,
) {
  const laneNode = laneNodeFor(lane);
  const label = (
    <>
      <span className="font-medium">{lane.label}</span>
      {lane.sublabel && (
        <span className="font-mono text-muted-foreground">{lane.sublabel}</span>
      )}
      {lane.status === "error" && (
        <span className="text-destructive">{lane.rawStatus || "failed"}</span>
      )}
    </>
  );
  if (!laneNode) {
    return <div className="mb-1 flex items-center gap-1.5 text-xs">{label}</div>;
  }
  return (
    <button
      type="button"
      onClick={() => onSelect(laneNode)}
      aria-pressed={lane.id === selectedNodeId}
      className={cn(
        "mb-1 flex w-fit cursor-pointer items-center gap-1.5 rounded-sm px-1 py-0.5 text-xs hover:bg-surface-subtle",
        lane.id === selectedNodeId && "bg-surface-subtle",
      )}
    >
      <ChevronRight className="size-3 shrink-0 text-muted-foreground" aria-hidden />
      {label}
    </button>
  );
}

/**
 * Fallback for a trace with no time axis.
 *
 * When the trigger user message cannot be resolved the server nulls every
 * offset at once, so there is nothing to lay bars out against. The nodes are
 * still ordered and still carry durations, so they are listed in sequence
 * instead of being dropped.
 */
export function TraceSequence({
  trace,
  selectedNodeId,
  onSelect,
}: {
  trace: NormalizedTrace;
  selectedNodeId: string | null;
  onSelect: (node: TraceNode) => void;
}) {
  const flatten = (nodes: TraceNode[], depth: number): Array<{ node: TraceNode; depth: number }> =>
    nodes.flatMap((node) => [
      { node, depth },
      ...flatten(node.children, depth + 1),
    ]);

  return (
    <div className="grid gap-2">
      <p className="text-xs text-muted-foreground">
        트리거 메시지를 찾지 못해 상대 시간을 알 수 없습니다 — 실행 순서로만 표시합니다.
      </p>
      <div className="rounded-lg border border-border p-2">
        {trace.lanes.map((lane) => (
          <div key={lane.id} className="border-t border-border py-2 first:border-t-0">
            {/* This view used to iterate lane.nodes only, which made the
                sub-agent's objective and summary unreachable here — no click
                got you there at all. */}
            {laneHeader(lane, selectedNodeId, onSelect)}
            <ol className="grid gap-1">
              {flatten(lane.nodes, 0).map(({ node, depth }) => (
                <li key={node.id} style={{ paddingLeft: `${depth * 12}px` }}>
                  <button
                    type="button"
                    onClick={() => onSelect(node)}
                    aria-pressed={node.id === selectedNodeId}
                    className={cn(
                      "w-full cursor-pointer rounded-sm px-1.5 py-0.5 text-left text-xs hover:bg-surface-subtle",
                      node.id === selectedNodeId && "bg-surface-subtle font-medium",
                      node.status === "error" && "text-destructive",
                    )}
                  >
                    {node.label}
                    <span className="ml-2 text-muted-foreground">
                      {formatMs(node.durationMs)}
                    </span>
                    {node.rawStatus && (
                      <span className="ml-2 text-muted-foreground">{node.rawStatus}</span>
                    )}
                  </button>
                </li>
              ))}
            </ol>
            <SubAgentInlineSummary lane={lane} />
          </div>
        ))}
      </div>
    </div>
  );
}
